import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Plus, X, Trash2, Flag } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { resolveMedia } from '../social/discovery';
const ReportModal = lazy(() => import('../moderation/ReportModal'));

const STORY_DURATION_IMG = 5000; // ms per image story
const STORY_DURATION_VIDEO = null; // null = use actual video duration

export function StoriesBar({ stories = [], currentUser, onAddStory, onStoriesChange }) {
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(null);
  const [storyIndex, setStoryIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);    // ms
  const [totalMs, setTotalMs] = useState(STORY_DURATION_IMG);
  const [paused, setPaused] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const lastTickRef = useRef(null);

  const selectedGroup = selectedGroupIndex !== null ? stories[selectedGroupIndex] : null;
  const currentStory = selectedGroup?.stories?.[storyIndex] ?? null;

  // ─── open / close ─────────────────────────────────────────────────────────
  const openGroup = useCallback((groupIdx, stIdx = 0) => {
    setSelectedGroupIndex(groupIdx);
    setStoryIndex(stIdx);
    setElapsed(0);
    setPaused(false);
  }, []);

  const closeViewer = useCallback(() => {
    setSelectedGroupIndex(null);
    setStoryIndex(0);
    setElapsed(0);
    clearInterval(timerRef.current);
  }, []);

  // ─── navigation ───────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (!selectedGroup) return;
    if (storyIndex < selectedGroup.stories.length - 1) {
      setStoryIndex(i => i + 1);
      setElapsed(0);
    } else {
      const nextGroup = selectedGroupIndex + 1;
      if (nextGroup < stories.length) {
        openGroup(nextGroup, 0);
      } else {
        closeViewer();
      }
    }
  }, [selectedGroup, storyIndex, selectedGroupIndex, stories.length, openGroup, closeViewer]);

  const goPrev = useCallback(() => {
    if (!selectedGroup) return;
    if (storyIndex > 0) {
      setStoryIndex(i => i - 1);
      setElapsed(0);
    } else if (selectedGroupIndex > 0) {
      const prevGroup = selectedGroupIndex - 1;
      openGroup(prevGroup, stories[prevGroup].stories.length - 1);
    }
  }, [selectedGroup, storyIndex, selectedGroupIndex, stories, openGroup]);

  // ─── timed progress (image) ───────────────────────────────────────────────
  useEffect(() => {
    if (!currentStory || paused) {
      clearInterval(timerRef.current);
      return;
    }
    const isVideo = currentStory.media_type === 'video' && currentStory.media_urls?.length > 0;
    if (isVideo) {
      // video drives progress via onTimeUpdate; don't run interval
      clearInterval(timerRef.current);
      return;
    }
    const dur = STORY_DURATION_IMG;
    setTotalMs(dur);
    clearInterval(timerRef.current);
    lastTickRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      setElapsed(prev => {
        const next = prev + delta;
        if (next >= dur) { clearInterval(timerRef.current); goNext(); return 0; }
        return next;
      });
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [currentStory, paused, goNext]);

  // ─── mark viewed ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentStory) return;
    if (currentStory.is_viewed) return;
    api.post(`/posts/stories/${currentStory.id}/view`).catch(() => {});
  }, [currentStory]);

  // ─── delete own story ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!currentStory) return;
    try {
      await api.delete(`/posts/${currentStory.id}`);
      toast.success('Story removed');
      if (onStoriesChange) onStoriesChange();
      closeViewer();
    } catch {
      toast.error('Could not delete story');
    }
  };

  // ─── video event handlers ─────────────────────────────────────────────────
  const onVideoTimeUpdate = useCallback((e) => {
    const { currentTime, duration } = e.currentTarget;
    if (duration > 0) {
      setTotalMs(duration * 1000);
      setElapsed(currentTime * 1000);
      if (currentTime >= duration - 0.1) goNext();
    }
  }, [goNext]);

  const onVideoLoaded = useCallback((e) => {
    setTotalMs(e.currentTarget.duration * 1000);
    setElapsed(0);
    if (!paused) e.currentTarget.play().catch(() => {});
  }, [paused]);

  // ─── press-and-hold to pause ──────────────────────────────────────────────
  const holdTimer = useRef(null);
  const onPointerDown = useCallback(() => {
    holdTimer.current = setTimeout(() => setPaused(true), 150);
  }, []);
  const onPointerUp = useCallback(() => {
    clearTimeout(holdTimer.current);
    setPaused(false);
  }, []);

  // sync pause to video element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused]);

  // ─── progress pct ─────────────────────────────────────────────────────────
  const progressPct = totalMs > 0 ? Math.min(100, (elapsed / totalMs) * 100) : 0;

  /* ─────────────────────── RENDER ─────────────────────────────────────────── */
  return (
    <>
      {/* ── Ring strip ── */}
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" data-testid="stories-bar">
        {/* Own avatar / Add Story */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <button
            onClick={onAddStory}
            className="relative w-16 h-16 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="add-story-btn"
            aria-label="Add Story"
          >
            {currentUser?.avatar ? (
              <img
                src={resolveMedia(currentUser.avatar)}
                alt={currentUser.display_name}
                className="w-full h-full rounded-full object-cover border-2 border-dashed border-border"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                <span className="text-xl font-bold text-muted-foreground">{currentUser?.display_name?.[0]}</span>
              </div>
            )}
            <span className="absolute bottom-0 right-0 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-background">
              <Plus className="w-3 h-3 text-white" />
            </span>
          </button>
          <span className="text-xs text-muted-foreground">Add Story</span>
        </div>

        {/* Other users */}
        {stories.map((group, idx) => {
          const hasUnseen = group.stories.some(s => !s.is_viewed);
          return (
            <button
              key={group.user.id}
              onClick={() => openGroup(idx)}
              className="flex flex-col items-center gap-1 flex-shrink-0 focus-visible:outline-none"
              data-testid={`story-${group.user.username}`}
            >
              <div className={`p-0.5 rounded-full ${hasUnseen ? 'bg-gradient-to-tr from-mg-purple to-accent' : 'bg-muted'}`}>
                <div className="p-0.5 bg-background rounded-full">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={resolveMedia(group.user.avatar)} />
                    <AvatarFallback>{group.user.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                </div>
              </div>
              <span className="text-xs truncate w-16 text-center">
                {group.user.id === currentUser?.id ? 'You' : group.user.display_name?.split(' ')[0]}
              </span>
            </button>
          );
        })}

        {stories.length === 0 && (
          <div className="flex items-center px-2 py-2 text-sm text-muted-foreground italic">
            No stories yet
          </div>
        )}
      </div>

      {/* ── Viewer modal ── */}
      <Dialog open={!!selectedGroup} onOpenChange={closeViewer}>
        <DialogContent className="max-w-sm w-full p-0 bg-black border-none overflow-hidden rounded-2xl">
          {selectedGroup && currentStory && (
            <div
              className="relative select-none"
              style={{ height: 'min(80dvh, 700px)' }}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              {/* Segmented progress bars */}
              <div className="absolute top-3 left-3 right-3 flex gap-1 z-30">
                {selectedGroup.stories.map((s, i) => (
                  <div key={s.id} className="h-0.5 flex-1 rounded-full overflow-hidden bg-white/30">
                    <div
                      className="h-full bg-white rounded-full transition-none"
                      style={{
                        width: i < storyIndex ? '100%'
                          : i === storyIndex ? `${progressPct}%`
                          : '0%'
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Header row */}
              <div className="absolute top-7 left-0 right-0 px-3 z-30 flex items-center justify-between">
                <Link
                  to={`/profile/${selectedGroup.user.username}`}
                  className="flex items-center gap-2"
                  onClick={closeViewer}
                >
                  <Avatar className="w-9 h-9 border-2 border-white/80">
                    <AvatarImage src={resolveMedia(selectedGroup.user.avatar)} />
                    <AvatarFallback>{selectedGroup.user.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-white text-sm leading-tight">{selectedGroup.user.display_name}</p>
                    <p className="text-[11px] text-white/60">
                      {new Date(currentStory.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  {currentStory.user_id === currentUser?.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                      className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white"
                      aria-label="Delete story"
                      data-testid="story-delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {currentStory.user_id !== currentUser?.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setReportOpen(true); }}
                      className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white/70"
                      aria-label="Report story"
                      data-testid="story-report-btn"
                    >
                      <Flag className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={closeViewer}
                    className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Story content */}
              <div className="absolute inset-0">
                {currentStory.media_urls?.length > 0 ? (
                  currentStory.media_type === 'video' ? (
                    <video
                      ref={videoRef}
                      key={currentStory.id}
                      src={resolveMedia(currentStory.media_urls[0])}
                      poster={currentStory.poster_url ? resolveMedia(currentStory.poster_url) : undefined}
                      className="w-full h-full object-contain bg-black"
                      autoPlay
                      playsInline
                      muted={false}
                      onTimeUpdate={onVideoTimeUpdate}
                      onLoadedMetadata={onVideoLoaded}
                      onEnded={goNext}
                    />
                  ) : (
                    <img
                      src={resolveMedia(currentStory.media_urls[0])}
                      alt=""
                      className="w-full h-full object-contain bg-black"
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-accent p-8">
                    <p className="text-white text-xl text-center font-heading">{currentStory.content}</p>
                  </div>
                )}
              </div>

              {/* Caption */}
              {currentStory.content && currentStory.media_urls?.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent z-10 pointer-events-none">
                  <p className="text-white text-sm">{currentStory.content}</p>
                </div>
              )}

              {/* Tap zones: left = prev, right = next */}
              <button
                className="absolute left-0 top-0 w-1/3 h-full z-20"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label="Previous story"
              />
              <button
                className="absolute right-0 top-0 w-1/3 h-full z-20"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label="Next story"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {currentStory && (
        <Suspense fallback={null}>
          <ReportModal
            open={reportOpen}
            onClose={() => setReportOpen(false)}
            targetType="post"
            targetId={currentStory.id}
            targetLabel="Story"
          />
        </Suspense>
      )}
    </>
  );
}

export default StoriesBar;
